import { BARE_CONTROL, BareTextField } from "@common/components/form"
import { Hint } from "@common/components/shared/hint"
import { Button } from "@common/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@common/components/ui/collapsible"
import { StatusDot, type StatusTone } from "@common/components/ui/status-dot"
import { Textarea } from "@common/components/ui/textarea"
import { formatCount } from "@common/lib/number"
import { cn } from "@common/lib/utils"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { ChevronDown, ChevronUp, Maximize2, Minimize2 } from "lucide-react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import {
  type FieldPath,
  type FieldValues,
  get,
  useController,
  useFormState,
} from "react-hook-form"
import { ListTabs } from "@/components/layout/list-tabs"

type Anchor = {
  id: string
  title: string
  mark: string | null
  element: HTMLElement
  reveal: () => void
}

type RegisterAnchor = (anchor: Anchor) => {
  first: boolean
  unregister: () => void
  jump: () => void
}

const AnchorRegistry = createContext<RegisterAnchor | null | undefined>(
  undefined,
)

const ANCHOR_GAP = 8

const BAR_INSET = 16

const MAX_TITLE_SHARE = 1 / 3

const RELEASE_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"]

const HEAD = cn(
  "sticky -top-4 z-10 -mx-4 bg-background px-4 transition-shadow",
  "data-[stuck=true]:shadow-xs",
)

const TITLED = "-mt-4 pt-4"

const ANCHOR_BAR = cn(
  "-mx-4 flex h-10 items-center overflow-x-auto px-4",
  "[scrollbar-width:none]",
)

const SECTIONS = "space-y-2"

const META = "truncate text-small-regular text-muted-foreground tabular-nums"

export const PANE_BANNER =
  "flex h-9 shrink-0 items-center gap-2 border-b text-small-regular"

export const PANE_CHIP = cn(
  "inline-flex h-7 w-auto max-w-full items-center gap-1.5 rounded-md border",
  "bg-background px-2 text-small-medium shadow-none",
  "focus:ring-0 focus:ring-offset-0",
)

const CHIP_INPUT = cn(
  BARE_CONTROL,
  "h-6 w-auto min-w-8 px-0 text-small-medium [field-sizing:content]",
  "aria-invalid:text-destructive",
)

const FIELD_ERROR = "block text-small-regular text-destructive"

const TITLE_BOX = cn(
  "-ml-2 block w-[calc(100%+0.5rem)] rounded-md border border-transparent",
  "px-2 py-1 text-h3",
)

const TITLE_TEXT = cn(TITLE_BOX, "whitespace-pre-wrap break-words")

const TITLE_INPUT = cn(
  TITLE_BOX,
  "min-h-0 resize-none bg-transparent shadow-none [field-sizing:content]",
  "hover:border-input focus-visible:border-ring focus-visible:ring-offset-0",
)

function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === "auto" || overflowY === "scroll") return node
  }
  return null
}

function pixels(value: string): number {
  return Number.parseFloat(value) || 0
}

function stickLine(head: HTMLElement, scroller: HTMLElement): number {
  return (
    scroller.getBoundingClientRect().top +
    pixels(getComputedStyle(scroller).paddingTop) +
    pixels(getComputedStyle(head).top)
  )
}

function pinnedHeight(head: HTMLElement | null, scroller: HTMLElement): number {
  if (!head) return pixels(getComputedStyle(scroller).paddingTop)
  const line = stickLine(head, scroller)
  return line + head.offsetHeight - scroller.getBoundingClientRect().top
}

function byPosition(left: Anchor, right: Anchor): number {
  const order = left.element.compareDocumentPosition(right.element)
  return order & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
}

function align(
  head: HTMLElement | null,
  scroller: HTMLElement,
  anchor: Anchor,
): void {
  const offset =
    anchor.element.getBoundingClientRect().top -
    scroller.getBoundingClientRect().top
  scroller.scrollTo({
    top:
      scroller.scrollTop + offset - pinnedHeight(head, scroller) - ANCHOR_GAP,
  })
}

function revealPill(bar: HTMLElement): void {
  const pill = bar.querySelector("[data-state=on]")
  if (!pill) return
  const edges = bar.getBoundingClientRect()
  const box = pill.getBoundingClientRect()
  const before = box.left - edges.left - BAR_INSET
  const after = box.right - edges.right + BAR_INSET
  if (before < 0) bar.scrollLeft += before
  else if (after > 0) bar.scrollLeft += Math.min(after, before)
}

function sectionStatus(
  title: string,
  done: boolean | undefined,
  optional: boolean,
  flagged: boolean,
): { tone: StatusTone; label: string } | null {
  if (flagged) return { tone: "warning", label: "Values to check" }
  if (done === undefined) return null
  if (done) return { tone: "success", label: `${title} completed` }
  return optional
    ? { tone: "neutral", label: `${title} optional` }
    : { tone: "warning", label: `${title} pending` }
}

export function PaneSections({
  anchors = true,
  header,
  jump,
  onJump,
  children,
}: {
  anchors?: boolean
  header?: React.ReactNode
  jump?: string
  onJump?: () => void
  children: React.ReactNode
}): React.ReactElement {
  const outer = useContext(AnchorRegistry)

  if (!anchors || outer !== undefined) {
    return (
      <>
        {header}
        <div className={SECTIONS}>{children}</div>
      </>
    )
  }

  return (
    <AnchoredSections header={header} jump={jump} onJump={onJump}>
      {children}
    </AnchoredSections>
  )
}

function AnchoredSections({
  header,
  jump,
  onJump,
  children,
}: {
  header?: React.ReactNode
  jump?: string
  onJump?: () => void
  children: React.ReactNode
}): React.ReactElement {
  const [anchors, setAnchors] = useState<readonly Anchor[]>([])
  const [active, setActive] = useState<string | null>(null)
  const [stuck, setStuck] = useState(false)
  const head = useRef<HTMLDivElement>(null)
  const title = useRef<HTMLDivElement>(null)
  const pills = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const registered = useRef<readonly Anchor[]>([])
  const pinned = useRef<Anchor | null>(null)
  const reached = useRef(false)
  const fit = useRef<(() => void) | null>(null)
  const jumped = useRef<string | undefined>(undefined)
  const anchored = anchors.length > 1
  const titled = header !== undefined
  const shown = active ?? anchors[0]?.id

  const go = useCallback((anchor: Anchor) => {
    anchor.reveal()
    setActive(anchor.id)
    pinned.current = anchor
    reached.current = true
    if (fit.current) {
      fit.current()
      return
    }
    const scroller = list.current && scrollParent(list.current)
    if (scroller) align(head.current, scroller, anchor)
  }, [])

  const queued = useRef<Anchor | null>(null)

  const request = useCallback(
    (anchor: Anchor) => {
      anchor.reveal()
      const earlier = queued.current
      if (earlier) {
        if (byPosition(anchor, earlier) < 0) queued.current = anchor
        return
      }
      queued.current = anchor
      requestAnimationFrame(() => {
        const target = queued.current
        queued.current = null
        if (target?.element.isConnected) go(target)
      })
    },
    [go],
  )

  const register = useCallback<RegisterAnchor>(
    (anchor) => {
      const first = registered.current.every(
        (entry) => byPosition(anchor, entry) < 0,
      )
      registered.current = [...registered.current, anchor]
      setAnchors((current) => [...current, anchor].sort(byPosition))
      return {
        first,
        unregister: () => {
          registered.current = registered.current.filter(
            (entry) => entry !== anchor,
          )
          setAnchors((current) => current.filter((entry) => entry !== anchor))
        },
        jump: () => request(anchor),
      }
    },
    [request],
  )

  useEffect(() => {
    const release = () => {
      pinned.current = null
    }
    for (const name of RELEASE_EVENTS) {
      document.addEventListener(name, release, { passive: true })
    }
    return () => {
      for (const name of RELEASE_EVENTS) {
        document.removeEventListener(name, release)
      }
    }
  }, [])

  useEffect(() => {
    const block = head.current
    const element = title.current
    const scroller = element && scrollParent(element)
    if (!block || !element || !scroller) return
    const measure = () => {
      const tall =
        element.offsetHeight > scroller.clientHeight * MAX_TITLE_SHARE
      const hidden =
        pixels(getComputedStyle(scroller).paddingTop) +
        element.offsetTop +
        element.offsetHeight
      block.style.top = tall ? `${-hidden}px` : ""
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const scroller = list.current && scrollParent(list.current)
    if (!scroller) return
    const spy = () => {
      const element = head.current
      const edge = element
        ? element.getBoundingClientRect().bottom
        : scroller.getBoundingClientRect().top + pinnedHeight(null, scroller)
      if (element) {
        const line = stickLine(element, scroller)
        const top = element.getBoundingClientRect().top
        setStuck(scroller.scrollTop > 0 && top <= line + 1)
      }
      if (pinned.current) return
      const passed = anchors.filter(
        (anchor) =>
          anchor.element.getBoundingClientRect().top <= edge + ANCHOR_GAP * 2,
      )
      setActive(passed.at(-1)?.id ?? null)
    }
    spy()
    scroller.addEventListener("scroll", spy, { passive: true })
    return () => scroller.removeEventListener("scroll", spy)
  }, [anchors])

  useEffect(() => {
    const sections = list.current
    const scroller = sections && scrollParent(sections)
    const last = anchors.at(-1)?.element
    if (!sections) return
    if (!scroller || !last || anchors.length < 2) {
      sections.style.paddingBottom = ""
      return
    }
    const measure = () => {
      const current = pixels(sections.style.paddingBottom)
      const overflows = scroller.scrollHeight - current > scroller.clientHeight
      const padding = pixels(getComputedStyle(scroller).paddingBottom)
      const room =
        scroller.clientHeight -
        pinnedHeight(head.current, scroller) -
        ANCHOR_GAP
      const tail =
        overflows || reached.current
          ? Math.max(0, room - last.offsetHeight - padding)
          : 0
      sections.style.paddingBottom = `${tail}px`
      const target = pinned.current
      if (target?.element.isConnected) align(head.current, scroller, target)
    }
    measure()
    fit.current = measure
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    observer.observe(sections)
    observer.observe(last)
    if (title.current) observer.observe(title.current)
    return () => {
      observer.disconnect()
      fit.current = null
    }
  }, [anchors])

  useEffect(() => {
    const element = pills.current
    if (!element || !shown) return
    const reveal = () => revealPill(element)
    reveal()
    const observer = new ResizeObserver(reveal)
    observer.observe(element)
    return () => observer.disconnect()
  }, [shown])

  useEffect(() => {
    if (jump === jumped.current) return
    const anchor = anchors.find((entry) => entry.title === jump)
    if (jump && !anchor) return
    jumped.current = jump
    if (!anchor) return
    go(anchor)
    onJump?.()
  }, [jump, anchors, go, onJump])

  const anchorOf = (id: string) => anchors.find((anchor) => anchor.id === id)

  const tabs = anchored && shown && (
    <div ref={pills} className={ANCHOR_BAR}>
      <ListTabs
        name="Sections"
        tabs={anchors.map((anchor) => anchor.id)}
        tab={shown}
        label={(id) => anchorOf(id)?.title ?? ""}
        marked={(id) => Boolean(anchorOf(id)?.mark)}
        markLabel={(id) => anchorOf(id)?.mark ?? ""}
        reselect
        onSelect={(id) => {
          const anchor = anchorOf(id)
          if (anchor) go(anchor)
        }}
      />
    </div>
  )

  return (
    <AnchorRegistry.Provider value={register}>
      {(titled || anchored) && (
        <div
          ref={head}
          data-stuck={stuck}
          className={cn(HEAD, titled && TITLED)}
        >
          {titled && <div ref={title}>{header}</div>}
          {tabs}
        </div>
      )}
      <div ref={list} className={SECTIONS}>
        {children}
      </div>
    </AnchorRegistry.Provider>
  )
}

type PaneSectionProps = {
  title: string
  meta?: React.ReactNode
  done?: boolean
  optional?: boolean
  flagged?: boolean
  action?: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

export function PaneSection<T extends FieldValues = FieldValues>({
  fields,
  ...props
}: PaneSectionProps & {
  fields?: readonly FieldPath<T>[]
}): React.ReactElement {
  return fields ? (
    <FormPaneSection<T> fields={fields} {...props} />
  ) : (
    <Section {...props} invalid={false} submitCount={0} />
  )
}

function FormPaneSection<T extends FieldValues>({
  fields,
  ...props
}: PaneSectionProps & {
  fields: readonly FieldPath<T>[]
}): React.ReactElement {
  const { errors, submitCount } = useFormState<T>({ name: fields })
  const invalid = fields.some((field) => get(errors, field) !== undefined)
  return <Section {...props} invalid={invalid} submitCount={submitCount} />
}

function Section({
  title,
  meta,
  done,
  optional = false,
  flagged = false,
  action,
  open,
  defaultOpen = false,
  onOpenChange,
  invalid,
  submitCount,
  children,
}: PaneSectionProps & {
  invalid: boolean
  submitCount: number
}): React.ReactElement {
  const register = useContext(AnchorRegistry)
  const id = useId()
  const [expanded, setExpanded] = useState(true)
  const root = useRef<HTMLDivElement>(null)
  const decided = useRef(false)
  const opening = useRef(defaultOpen)
  const changed = useRef(onOpenChange)
  const failed = useRef(invalid)
  const seen = useRef(submitCount)
  const reveal = useRef(() => {
    setExpanded(true)
    changed.current?.(true)
  })
  const jump = useRef(reveal.current)
  const status = sectionStatus(title, done, optional, flagged)
  const mark = status?.tone === "warning" ? status.label : null
  const toggle = (next: boolean) => {
    setExpanded(next)
    onOpenChange?.(next)
  }

  useLayoutEffect(() => {
    changed.current = onOpenChange
    failed.current = invalid
  })

  useEffect(() => {
    if (submitCount === seen.current) return
    seen.current = submitCount
    if (failed.current) jump.current()
  }, [submitCount])

  useLayoutEffect(() => {
    const element = root.current
    if (!register || !element) return
    const registration = register({
      id,
      title,
      mark,
      element,
      reveal: reveal.current,
    })
    jump.current = registration.jump
    if (!decided.current) {
      decided.current = true
      const start = registration.first || opening.current
      setExpanded(start)
      changed.current?.(start)
    }
    return () => {
      registration.unregister()
      jump.current = reveal.current
    }
  }, [register, id, title, mark])

  return (
    <Collapsible
      ref={root}
      open={open ?? expanded}
      onOpenChange={toggle}
      className="space-y-2"
    >
      <div className="flex h-8 items-center gap-2 rounded-md bg-sidebar-accent/50 px-2 hover:bg-sidebar-accent">
        <h3 className="flex min-w-0 flex-1 self-stretch">
          <CollapsibleTrigger className="group flex min-w-0 flex-1 items-center gap-2 text-left">
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
            <span className="text-body-medium">{title}</span>
            {status ? (
              <span className="ml-auto flex min-w-0 items-center gap-2">
                {meta && <span className={META}>{meta}</span>}
                <Hint text={status.label}>
                  <StatusDot tone={status.tone} />
                </Hint>
                <span className="sr-only">{status.label}</span>
              </span>
            ) : (
              meta && <span className={META}>{meta}</span>
            )}
          </CollapsibleTrigger>
        </h3>
        {action}
      </div>
      <CollapsibleContent className="pb-2 pl-2">
        <AnchorRegistry.Provider value={null}>
          {children}
        </AnchorRegistry.Provider>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function PaneTitle({
  title,
  children,
}: {
  title: React.ReactNode
  children?: React.ReactNode
}): React.ReactElement {
  return (
    <div className="space-y-1.5 pb-3">
      <h3 className="break-words text-h3">{title}</h3>
      {children}
    </div>
  )
}

function normalised(text: string, multiline: boolean): string {
  return (multiline ? text : text.replace(/\s+/g, " ")).trim()
}

export function PaneTitleInput({
  label,
  shown,
  placeholder,
  editable,
  optional = false,
  multiline = false,
  invalid = false,
  autoSelect = false,
  submitOnEnter = false,
  onCommit,
}: {
  label: string
  shown: string
  placeholder?: string
  editable: boolean
  optional?: boolean
  multiline?: boolean
  invalid?: boolean
  autoSelect?: boolean
  submitOnEnter?: boolean
  onCommit: (typed: string) => void
}): React.ReactElement {
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const input = ref.current
    if (input && document.activeElement !== input) input.value = shown
  }, [shown])

  useEffect(() => {
    const input = ref.current
    if (autoSelect && input) {
      input.focus()
      input.select()
    }
  }, [autoSelect])

  if (!editable) {
    return <span className={TITLE_TEXT}>{shown}</span>
  }

  return (
    <Textarea
      ref={ref}
      aria-label={label}
      aria-invalid={invalid}
      defaultValue={shown}
      placeholder={placeholder}
      rows={1}
      className={cn(TITLE_INPUT, invalid && "border-destructive")}
      onBlur={(event) => {
        const typed = normalised(event.target.value, multiline)
        const changed = typed !== normalised(shown, multiline)
        if (changed && (typed || optional)) onCommit(typed)
        else event.target.value = shown
      }}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return
        if (event.key === "Enter" && !multiline) {
          event.preventDefault()
          const form = event.currentTarget.form
          event.currentTarget.blur()
          if (submitOnEnter) form?.requestSubmit()
        }
        if (event.key === "Escape") {
          event.currentTarget.value = shown
          event.currentTarget.blur()
        }
      }}
    />
  )
}

export function PaneTitleField<T extends FieldValues>({
  name,
  label,
  placeholder,
  editable,
  optional = false,
}: {
  name: FieldPath<T>
  label: string
  placeholder?: string
  editable: boolean
  optional?: boolean
}): React.ReactElement {
  const { field, fieldState } = useController<T>({ name })

  return (
    <>
      <PaneTitleInput
        label={label}
        shown={String(field.value ?? "")}
        placeholder={placeholder}
        editable={editable}
        optional={optional}
        invalid={fieldState.invalid}
        submitOnEnter
        onCommit={(typed) => {
          field.onChange(typed)
          field.onBlur()
        }}
      />
      {fieldState.error?.message && (
        <span role="alert" className={FIELD_ERROR}>
          {fieldState.error.message}
        </span>
      )}
    </>
  )
}

export function PaneChipField<T extends FieldValues>({
  name,
  label,
  placeholder,
  editable,
}: {
  name: FieldPath<T>
  label: string
  placeholder?: string
  editable: boolean
}): React.ReactElement {
  return (
    <PaneChip>
      <span className="text-muted-foreground">{label}</span>
      <BareTextField<T>
        name={name}
        aria-label={label}
        placeholder={placeholder}
        disabled={!editable}
        size="inline"
        className={CHIP_INPUT}
      />
    </PaneChip>
  )
}

export function PaneFieldError<T extends FieldValues>({
  name,
}: {
  name: FieldPath<T>
}): React.ReactNode {
  const { errors } = useFormState<T>({ name })
  const message = get(errors, name)?.message

  return (
    typeof message === "string" && (
      <p role="alert" className={FIELD_ERROR}>
        {message}
      </p>
    )
  )
}

export function PaneChip({
  title,
  className,
  children,
}: {
  title?: string
  className?: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <span title={title} className={cn(PANE_CHIP, className)}>
      {children}
    </span>
  )
}

export function PaneStepper({
  index,
  total,
  noun,
  suffix,
  onPrevious,
  onNext,
}: {
  index: number
  total: number
  noun?: string
  suffix?: string
  onPrevious: (() => void) | null
  onNext: (() => void) | null
}): React.ReactElement {
  const target = noun ? ` ${noun}` : ""
  return (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      {index >= 0 && (
        <span className="text-small-medium text-muted-foreground tabular-nums">
          {`${formatCount(index + 1)} of ${formatCount(total)}${suffix ? ` ${suffix}` : ""}`}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Previous${target}`}
        disabled={!onPrevious}
        onClick={onPrevious ?? undefined}
      >
        <ChevronUp />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Next${target}`}
        disabled={!onNext}
        onClick={onNext ?? undefined}
      >
        <ChevronDown />
      </Button>
    </div>
  )
}

export function PaneFooter({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="sticky bottom-0 mt-auto flex shrink-0 items-center justify-between gap-4 border-t bg-background p-4">
      {children}
    </div>
  )
}

export function ExpandToggle(): React.ReactElement {
  const { wide } = useSearch({ strict: false })
  const navigate = useNavigate()

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={wide ? "Show the list" : "Expand"}
      onClick={() =>
        navigate({
          to: ".",
          search: (previous) => ({
            ...previous,
            wide: wide ? undefined : true,
          }),
          replace: true,
        })
      }
    >
      {wide ? <Minimize2 /> : <Maximize2 />}
    </Button>
  )
}
